import { useMemo } from "react"

import { useTranslation } from "react-i18next"
import { Button, Icon, Label, Segment } from "semantic-ui-react"
import type { OpenAPI } from "simplydo/interfaces"

import util from "utils/utils"

type StageSegmentProps = {
  stage: string
  impactStatement?: OpenAPI.Schemas["ImpactStatement"]
  onView?: () => void
  onCreate?: () => void
  onDelete?: () => void
}

export const StageSegment = ({ stage, impactStatement, onView, onCreate, onDelete }: StageSegmentProps) => {
  const { t } = useTranslation()

  const segmentMap = useMemo(
    () => ({
      userEstimate: {
        name: "User Estimate",
        icon: "wpforms",
        description: `This estimate is based on the estimation of the user who created the ${t("generic.idea")}.`,
      },
      projectEstimate: {
        name: "Project Estimate",
        icon: "object group outline",
        description: ` This estimate is based on the estimation as part of the ${t("generic.idea")}'s assessment. It is only visible to project assignees and ${t("generic.challenge")} managers.`,
        create: "Create estimate",
      },
      finalOutcome: {
        name: "Final Outcome",
        icon: "line graph",
        description: `This statement is used to track the realised ${t("generic.impact")} of this ${t("generic.idea")}. It is only visible to project assignees and ${t("generic.challenge")} managers.`,
        create: "Create outcome",
      },
    }),
    [t],
  )

  const def = useMemo(() => segmentMap[stage], [segmentMap, stage])
  return (
    <Segment secondary={!impactStatement}>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <div>
          <h3>
            <Icon name={def.icon} /> {def.name}
          </h3>
          {def.description}
        </div>
        {stage === "userEstimate" ? (
          impactStatement ? (
            <>
              <div>
                <Label>
                  {util.pluralise(
                    Object.keys(impactStatement.impacts).length,
                    t("generic.impact"),
                    t("generic.impacts"),
                  )}
                </Label>
              </div>
              <div style={{ display: "flex", flex: 1 }}>
                <Button style={{ flex: 1 }} onClick={onView}>
                  View details
                </Button>
              </div>
            </>
          ) : null
        ) : impactStatement ? (
          <>
            <div>
              <Label>
                {util.pluralise(Object.keys(impactStatement.impacts).length, t("generic.impact"), t("generic.impacts"))}
              </Label>
            </div>
            <div style={{ display: "flex", flex: 1 }}>
              <Button
                style={{ flex: 1 }}
                onClick={() => {
                  onView()
                }}
              >
                <Icon name="pencil" />
                Edit
              </Button>
              <Button
                icon
                basic
                onClick={() => {
                  util
                    .confirm(
                      "Delete final outcome?",
                      "Are you sure you want to delete the final outcome statement? This action is instant and irreversible.",
                    )
                    .then(() => {
                      onDelete()
                    })
                    .catch(() => {})
                }}
              >
                <Icon name="trash" />
              </Button>
            </div>
          </>
        ) : (
          <Button
            secondary
            onClick={() => {
              onCreate()
            }}
          >
            <Icon name="plus" />
            {def.create}
          </Button>
        )}
      </div>
    </Segment>
  )
}
